/**
 * ScoutPlayerCard.jsx
 * 
 * Phase 2F5 - Extracted from GMRosterBuilderV2
 * P1.3 Beta v2 - Expandable rows with collapsed/expanded states
 * 
 * Collapsed: matchup, game time, position
 * Expanded: prop details, edge visualization, select button
 * 
 * BSL Compliant: Monochrome defense display, no red/green.
 */

import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { LabeledEdgeBar } from '../viz';
import { POSITION_COLORS } from './RosterSlot';

/**
 * @param {Object} props
 * @param {Object} props.player - Player data from slate
 * @param {function} props.onSelect - Callback when player is selected
 */
const ScoutPlayerCard = ({ player, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const posColor = POSITION_COLORS[player.position] || '#64748B';
  const primaryProp = player.props?.[0];
  // Stability gating: only compute defense label if valid data exists
  const hasValidDefRank = player.passDefRank && player.passDefRank > 0 && player.passDefRank <= 32;
  const defLabel = hasValidDefRank 
    ? (player.passDefRank >= 25 ? 'Soft' : player.passDefRank <= 10 ? 'Tough' : '')
    : '';
  
  // Format game time if available
  const gameTime = player.gameTime 
    ? new Date(player.gameTime).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <div className="relative bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: posColor }} />
      
      {/* Collapsed Header - Always visible */}
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 pl-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
            style={{ backgroundColor: posColor }}
          >
            {player.position}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-slate-900 truncate">{player.name}</h3>
            <p className="text-xs text-slate-500">
              {player.team} vs {player.opponent}
              {gameTime && <span className="ml-1">• {gameTime}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Stability gating: only show defense badge if valid data */}
          {hasValidDefRank && player.opponent && (
            <div className="flex items-center gap-1 bg-slate-50 rounded px-2 py-1">
              <Shield size={12} className="text-slate-400" />
              <span className={`text-xs font-bold ${defLabel === 'Soft' ? 'text-indigo-600' : 'text-slate-500'}`}>
                #{player.passDefRank}
              </span>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp size={18} className="text-slate-400" />
          ) : (
            <ChevronDown size={18} className="text-slate-400" />
          )}
        </div>
      </button>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-3 border-t border-slate-100">
          {/* Primary prop edge display */}
          {primaryProp && (
            <div className="bg-slate-50 rounded-lg p-2 mt-3 mb-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-[10px] font-semibold text-slate-400 uppercase">{primaryProp.stat}</span>
                <span className="text-xs text-slate-500">
                  Avg: <span className="font-bold">{primaryProp.playerSeasonAvg?.toFixed(1) || '--'}</span>
                  {' • '}Line: <span className="font-bold">{primaryProp.line}</span>
                </span>
              </div>
              <LabeledEdgeBar 
                value={primaryProp.edgeValue} 
                label="Edge" 
                maxValue={30} 
              />
            </div>
          )}
          
          {/* Strategies count indicator */}
          {player.props?.length > 1 && (
            <p className="text-xs text-slate-500 mb-2">
              +{player.props.length - 1} more strateg{player.props.length > 2 ? 'ies' : 'y'} available
            </p>
          )}
          
          <button 
            onClick={() => onSelect(player)}
            className="w-full py-2.5 bg-indigo-600 text-white font-semibold rounded-lg text-sm 
                       transition-all active:scale-[0.98] hover:bg-indigo-700"
          >
            Select Player
          </button>
        </div>
      )}
    </div>
  );
};

export default ScoutPlayerCard;